import type { ButtonHTMLAttributes, ReactNode } from 'react';

export interface TouchButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode;
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  loading?: boolean;
  icon?: ReactNode;
  iconPosition?: 'left' | 'right';
}

/**
 * Mobile-optimized button with proper touch targets
 *
 * Features:
 * - Minimum 44px height (iOS guidelines)
 * - Active state feedback
 * - Loading state
 * - Icon support
 * - Multiple variants and sizes
 *
 * @example
 * <TouchButton
 *   variant="primary"
 *   onClick={() => console.log('Tapped')}
 *   loading={isLoading}
 * >
 *   Submit
 * </TouchButton>
 */
export function TouchButton({
  children,
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  loading = false,
  icon,
  iconPosition = 'left',
  className = '',
  disabled,
  style,
  ...props
}: TouchButtonProps) {
  const baseStyles: React.CSSProperties = {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 500,
    borderRadius: 'var(--sol-radius-lg)',
    transition: 'all 0.15s ease',
    opacity: disabled || loading ? 0.5 : 1,
    cursor: disabled || loading ? 'not-allowed' : 'pointer',
    width: fullWidth ? '100%' : undefined,
  };

  const variantStyles: Record<string, React.CSSProperties> = {
    primary: {
      background: 'var(--gradient-btn)',
      color: '#fff',
      border: 'none',
    },
    secondary: {
      background: 'var(--bg-card-solid)',
      color: 'var(--text-secondary)',
      border: '1px solid var(--border-cyan-10)',
    },
    ghost: {
      background: 'transparent',
      color: 'var(--text-secondary)',
      border: 'none',
    },
    danger: {
      background: 'var(--red)',
      color: '#fff',
      border: 'none',
    },
  };

  const sizeStyles: Record<string, React.CSSProperties> = {
    sm: { minHeight: '44px', padding: '8px 16px', fontSize: '14px', gap: '8px' },
    md: { minHeight: '44px', padding: '12px 24px', fontSize: '16px', gap: '8px' },
    lg: { minHeight: '52px', padding: '16px 32px', fontSize: '18px', gap: '12px' },
  };

  const combinedStyle: React.CSSProperties = {
    ...baseStyles,
    ...variantStyles[variant],
    ...sizeStyles[size],
    ...style,
  };

  return (
    <button
      className={className}
      style={combinedStyle}
      disabled={disabled || loading}
      {...props}
    >
      {loading && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 'inherit',
            background: 'inherit',
          }}
        >
          <div
            style={{
              width: '20px',
              height: '20px',
              border: '2px solid currentColor',
              borderTopColor: 'transparent',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }}
          />
        </div>
      )}
      <span
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'inherit',
          opacity: loading ? 0 : 1,
        }}
      >
        {icon && iconPosition === 'left' && <span>{icon}</span>}
        {children}
        {icon && iconPosition === 'right' && <span>{icon}</span>}
      </span>
    </button>
  );
}
